import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Search } from 'lucide-react';
import { OrderItem, Product } from '../types';
import { useI18n } from '../hooks/useI18n';

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: OrderItem) => void;
  item?: OrderItem;
  mode: 'create' | 'edit';
  currency: string;
  products?: Product[];
}

const OrderItemModal: React.FC<ProductModalProps> = ({
  isOpen,
  onClose,
  onSave,
  item,
  mode,
  currency,
  products = []
}) => {
  const { t, isRTL } = useI18n();
  const [formData, setFormData] = useState<OrderItem>({
    companyId: item?.companyId || '',
    code: '',
    description: '',
    unitPrice: 0,
    unity: '',
    quantity: 1,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Filter products based on search term
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (item && mode === 'edit') {
      setFormData({
        ...item
      });
    } else {
      setFormData({
        companyId: item?.companyId || '',
        code: '',
        description: '',
        unitPrice: 0,
        unity: '',
        quantity: 1,
      });
    }
    setErrors({});
  }, [item, mode, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.description.trim()) {
      newErrors.description = t('productModal.descriptionRequired');
    }

    if (formData.unitPrice <= 0) {
      newErrors.unitPrice = t('productModal.priceRequired');
    }

    if (formData.quantity <= 0) {
      newErrors.quantity = t('productModal.quantityRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  const handleInputChange = (field: keyof OrderItem, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const calculateTotal = () => {
    const total = formData.unitPrice * formData.quantity;
    // Round to 2 decimal places to avoid floating point issues
    return Math.round(total * 100) / 100;
  };

  const handleProductSelect = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setFormData(prev => ({
        ...prev,
        productId: product.id,
        code: product.code,
        description: product.name,
        unitPrice: product.unitPrice,
        unity: product.unity || 'u'
      }));
      // Clear errors
      setErrors({});
      setSearchTerm(product.name);
      setIsDropdownOpen(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === 'create' ? t('productModal.addProduct') : t('productModal.editProduct')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Product Selection */}
          {products.length > 0 && (
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('invoices.selectProduct')}
              </label>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder={t('invoices.selectProductPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsDropdownOpen(true);
                  }}
                  onFocus={() => setIsDropdownOpen(true)}
                />
              </div>

              {isDropdownOpen && searchTerm && (
                <ul className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => (
                      <li
                        key={product.id}
                        className="cursor-pointer select-none relative py-2 pl-3 pr-3 pr-9 hover:bg-blue-50"
                        onClick={() => handleProductSelect(product.id)}
                      >
                        <div className="flex justify-between">
                          <span className="font-medium block truncate">{product.name}</span>
                          <span className="text-gray-500">{currency}{product.unitPrice}</span>
                        </div>
                        <span className="text-xs text-gray-400 block truncate">{product.code}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-gray-500 cursor-default select-none relative py-2 pl-3 pr-9">
                      No products found
                    </li>
                  )}
                </ul>
              )}

              <div className="relative flex py-4 items-center">
                <div className="flex-grow border-t border-gray-300"></div>
                <span className="flex-shrink-0 mx-4 text-gray-400 text-xs uppercase">{t('common.or')}</span>
                <div className="flex-grow border-t border-gray-300"></div>
              </div>
            </div>
          )}

          {/* Product Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('productModal.productCode')}
            </label>
            <input
              type="text"
              value={formData.code || ''}
              onChange={(e) => handleInputChange('code', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('productModal.productCodePlaceholder')}
            />
          </div>

          {/* Product Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('productModal.description')} *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder={t('productModal.descriptionPlaceholder')}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Price and Quantity Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('productModal.unitPrice')} *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.unitPrice}
                onChange={(e) => handleInputChange('unitPrice', parseFloat(e.target.value) || 0)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.unitPrice ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="0.00"
              />
              {errors.unitPrice && (
                <p className="mt-1 text-sm text-red-600">{errors.unitPrice}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('productModal.quantity')} *
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.quantity}
                onChange={(e) => handleInputChange('quantity', parseFloat(e.target.value) || 1)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.quantity ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="1.00"
              />
              {errors.quantity && (
                <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('productModal.unit')}
              </label>
              <input
                type="text"
                value={formData.unity || ''}
                onChange={(e) => handleInputChange('unity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('productModal.unitPlaceholder')}
              />
            </div>
          </div>

          {/* Total Calculation */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">{t('productModal.total')}:</span>
              <span className="text-lg font-bold text-gray-900">
                {currency}{calculateTotal().toFixed(2)}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {formData.quantity} × {currency}{formData.unitPrice.toFixed(2)} = {currency}{calculateTotal().toFixed(2)}
            </div>
          </div>

          {/* Form Actions */}
          <div className={`flex items-center justify-end ${isRTL ? 'space-x-reverse' : 'space-x-3'} pt-4 border-t border-gray-200`}>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('productModal.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {mode === 'create' ? t('productModal.addProduct') : t('productModal.updateProduct')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderItemModal;
